import {
  ENEMY_INTRO_COPY,
  MECHANIC_HINT_COPY,
  TUTORIAL_STEPS,
  type TutorialPersistedState,
  type TutorialStepId,
  type TutorialStatus
} from './tutorialStorage'

export type TutorialEvent =
  | { type: 'MATCH_BEGIN' }
  | { type: 'TOWER_PLACED' }
  | { type: 'WAVE_STARTED' }
  | { type: 'WAVE_READY' }
  | { type: 'TOWER_UPGRADED' }
  | { type: 'TARGETING_CHANGED' }
  | { type: 'CONTROL_TOWER_PLACED' }
  | { type: 'DISMISS_INFO' }
  | { type: 'SKIP' }
  | { type: 'REPLAY' }
  | { type: 'MARK_ENEMY_SEEN'; family: string }
  | { type: 'MARK_MECHANIC_SEEN'; mechanic: string }

export interface TutorialViewModel {
  status: TutorialStatus
  step: TutorialStepId
  active: boolean
  message: string
  highlight: 'fire' | 'start' | 'upgrade' | 'targeting' | null
  canSkip: boolean
  enemyIntro: string | null
  mechanicHint: string | null
  seenEnemyFamilies: string[]
  seenMechanicHints: string[]
}

const STEP_COPY: Record<Exclude<TutorialStepId, 'done'>, string> = {
  build: 'Build a tower from the tray.',
  startWave: 'Start Wave 1 when you are ready.',
  pathInfo: 'Enemies follow the path. Stop them before the exit.',
  upgrade: 'Upgrade a tower to increase its power.',
  targeting: 'Change TARGET to control what this tower attacks.',
  controlHint: 'Frost and Poison slow enemies — useful against FAST.'
}

function nextStep(step: TutorialStepId): TutorialStepId {
  const index = TUTORIAL_STEPS.indexOf(step)
  if (index < 0 || index >= TUTORIAL_STEPS.length - 1) return 'done'
  return TUTORIAL_STEPS[index + 1]
}

function emptyHints(base?: string[]): string[] {
  return base ? [...base] : []
}

export function initialTutorialState(
  persisted?: TutorialPersistedState
): TutorialPersistedState {
  if (!persisted) {
    return {
      status: 'active',
      step: 'build',
      seenEnemyFamilies: [],
      seenMechanicHints: []
    }
  }
  if (persisted.status === 'completed' || persisted.status === 'skipped') {
    return {
      status: persisted.status,
      step: 'done',
      seenEnemyFamilies: [...persisted.seenEnemyFamilies],
      seenMechanicHints: emptyHints(persisted.seenMechanicHints)
    }
  }
  return {
    status: 'active',
    step: persisted.step === 'done' ? 'build' : persisted.step,
    seenEnemyFamilies: [...persisted.seenEnemyFamilies],
    seenMechanicHints: emptyHints(persisted.seenMechanicHints)
  }
}

/**
 * Pure tutorial reducer. GameSession remains combat authority —
 * this only tracks UI onboarding progression.
 */
export function reduceTutorial(
  state: TutorialPersistedState,
  event: TutorialEvent
): TutorialPersistedState {
  if (event.type === 'REPLAY') {
    return {
      status: 'active',
      step: 'build',
      seenEnemyFamilies: [],
      seenMechanicHints: []
    }
  }

  if (event.type === 'SKIP') {
    return {
      status: 'skipped',
      step: 'done',
      seenEnemyFamilies: state.seenEnemyFamilies,
      seenMechanicHints: state.seenMechanicHints ?? []
    }
  }

  if (event.type === 'MARK_ENEMY_SEEN') {
    if (state.seenEnemyFamilies.includes(event.family)) return state
    return {
      ...state,
      seenEnemyFamilies: [...state.seenEnemyFamilies, event.family]
    }
  }

  if (event.type === 'MARK_MECHANIC_SEEN') {
    const seen = state.seenMechanicHints ?? []
    if (seen.includes(event.mechanic)) return state
    return {
      ...state,
      seenMechanicHints: [...seen, event.mechanic]
    }
  }

  if (state.status !== 'active') return state

  if (event.type === 'MATCH_BEGIN') {
    return { ...state, step: 'build' }
  }

  switch (state.step) {
    case 'build':
      if (event.type === 'TOWER_PLACED') {
        return { ...state, step: nextStep('build') }
      }
      return state
    case 'startWave':
      if (event.type === 'WAVE_STARTED') {
        return { ...state, step: nextStep('startWave') }
      }
      return state
    case 'pathInfo':
      if (event.type === 'DISMISS_INFO' || event.type === 'WAVE_READY') {
        return { ...state, step: nextStep('pathInfo') }
      }
      return state
    case 'upgrade':
      if (event.type === 'TOWER_UPGRADED') {
        return { ...state, step: nextStep('upgrade') }
      }
      return state
    case 'targeting':
      if (event.type === 'TARGETING_CHANGED' || event.type === 'DISMISS_INFO') {
        return { ...state, step: nextStep('targeting') }
      }
      return state
    case 'controlHint':
      if (
        event.type === 'CONTROL_TOWER_PLACED' ||
        event.type === 'DISMISS_INFO' ||
        event.type === 'WAVE_STARTED'
      ) {
        return {
          status: 'completed',
          step: 'done',
          seenEnemyFamilies: state.seenEnemyFamilies,
          seenMechanicHints: state.seenMechanicHints ?? []
        }
      }
      return state
    default:
      return state
  }
}

export function tutorialViewModel(
  state: TutorialPersistedState,
  enemyIntroFamily?: string | null,
  mechanicId?: string | null
): TutorialViewModel {
  const active = state.status === 'active' && state.step !== 'done'
  const highlight =
    state.step === 'build'
      ? 'fire'
      : state.step === 'startWave'
        ? 'start'
        : state.step === 'upgrade'
          ? 'upgrade'
          : state.step === 'targeting'
            ? 'targeting'
            : null

  const enemyIntro =
    enemyIntroFamily && ENEMY_INTRO_COPY[enemyIntroFamily]
      ? ENEMY_INTRO_COPY[enemyIntroFamily]
      : null

  const mechanicHint =
    mechanicId && MECHANIC_HINT_COPY[mechanicId] ? MECHANIC_HINT_COPY[mechanicId] : null

  return {
    status: state.status,
    step: state.step,
    active,
    message: active && state.step !== 'done' ? STEP_COPY[state.step] : '',
    highlight,
    canSkip: active,
    enemyIntro,
    mechanicHint,
    seenEnemyFamilies: state.seenEnemyFamilies,
    seenMechanicHints: state.seenMechanicHints ?? []
  }
}

export function pickEnemyIntro(
  chipIds: string[],
  seen: string[]
): string | null {
  for (const id of chipIds) {
    if (id === 'normal') continue
    if (!ENEMY_INTRO_COPY[id]) continue
    if (!seen.includes(id)) return id
  }
  return null
}

/** Pick AA/Mage hint only when the tower is on the stage allow-list and content appears. */
export function pickMechanicHint(
  options: {
    allowedTowerIds: string[] | undefined
    chipIds: string[]
    seen: string[]
  }
): string | null {
  const allowed = options.allowedTowerIds
  const has = (id: string) => !allowed || allowed.includes(id)
  if (
    has('aa') &&
    options.chipIds.some((id) => id === 'flyer' || id === 'air_boss') &&
    !options.seen.includes('aa')
  ) {
    return 'aa'
  }
  if (
    has('mage') &&
    options.chipIds.some((id) => id === 'tank' || id === 'shield' || id === 'boss') &&
    !options.seen.includes('mage')
  ) {
    return 'mage'
  }
  return null
}
